'use client';

import { CircleHelp } from 'lucide-react';
import { useMemo, useState } from 'react';
import React from 'react';

import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const CONVERSION_RATE = 0.1;

export function CommissionEstimator() {
  const [brokerCount, setBrokerCount] = useState(10);
  const [tenantCount, setTenantCount] = useState(50);
  const payout = useMemo(
    () => Math.round(brokerCount * tenantCount * CONVERSION_RATE * 32.5),
    [brokerCount, tenantCount]
  );

  return (
    <div className='flex flex-col gap-8'>
      <TrackedSlider count={brokerCount} setCount={setBrokerCount} label='How many brokers do you have?' />
      <TrackedSlider count={tenantCount} setCount={setTenantCount} label='How many tenants do they contact per day?' />
      <div className='flex items-center gap-4 text-2xl font-bold'>
        <span>You could be making ${payout.toLocaleString('en-US')} per month!</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <CircleHelp size={16} />
            </TooltipTrigger>
            <TooltipContent>
              <span>Assumes a 10% conversion rate</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}

type TrackedSliderProps = {
  count: number;
  setCount: (value: number) => void;
  label: string;
};

function TrackedSlider({ count, setCount, label }: TrackedSliderProps) {
  return (
    <div className='flex flex-col gap-6'>
      <Label className='font-semibold text-lg'>{label}</Label>
      <div className='flex flex-col gap-2'>
        <Slider value={[count]} onValueChange={(e) => setCount(+e)} max={100} step={1} className='relative z-10' />
        <div className='relative flex justify-between'>
          <div className='absolute top-0 left-4 right-1'>
            <span className='absolute w-[5rem] text-center bg-primary/95' style={{ left: `calc(${count}% - 3rem)` }}>
              {count}
            </span>
          </div>
          <span>0</span>
          <span>50</span>
          <span>100</span>
        </div>
      </div>
    </div>
  );
}
